import { useState } from "react";
import { Mail, Send, MapPin, Clock, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE } from "@/data/site";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contatti() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm(initial);
    } catch {
      toast.error("Si è verificato un errore. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#1E1E1E] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors";

  return (
    <div data-testid="contatti-page">
      <Seo
        title="Contatti | TopAffiliation"
        description="Contatta il team di TopAffiliation per informazioni sul programma di affiliazione su scommesse, casinò e trading. Supporto 24/7."
        path="/contatti"
      />
      <section className="relative pt-36 pb-14 md:pt-44 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 grid-overlay opacity-50" />
        <div className="absolute -z-10 top-16 left-1/4 h-64 w-64 rounded-full bg-brand-red/15 blur-3xl" />
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Contatti</span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl leading-[1.05]">
              Parla con il nostro <span className="text-gradient">team</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
              Hai domande sul programma di affiliazione o hai bisogno di supporto?
              Scrivici: il nostro team è a tua disposizione.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Reveal className="lg:col-span-2">
            <form onSubmit={onSubmit} data-testid="contact-form" className="card-surface p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-white/70 mb-2 block">Nome e cognome *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-name"
                    className={inputCls}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-email"
                    className={inputCls}
                    placeholder="mario@esempio.it"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Oggetto</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-subject"
                  className={inputCls}
                  placeholder="Di cosa vuoi parlare?"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Messaggio *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  rows={5}
                  className={inputCls}
                  placeholder="Scrivi qui il tuo messaggio..."
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand-red px-7 py-3 text-sm font-semibold text-white hover:bg-brand-redDark hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Invio in corso..." : "Invia messaggio"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5">
              <div className="card-surface p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-red/10 text-brand-red">
                  <Mail className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading font-semibold">Email</h3>
                <a href={`mailto:${SITE.email}`} className="mt-1 block text-sm text-white/60 hover:text-brand-red">
                  {SITE.email}
                </a>
              </div>
              <div className="card-surface p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-rose/10 text-brand-rose">
                  <Clock className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading font-semibold">Supporto 24/7</h3>
                <p className="mt-1 text-sm text-white/60">
                  Assistenza esperta e qualificata, disponibile ogni giorno.
                </p>
              </div>
              <div className="card-surface p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-red/10 text-brand-red">
                  <MapPin className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading font-semibold">Made in Italy</h3>
                <p className="mt-1 text-sm text-white/60">
                  Il primo Network di affiliazione italiano su gambling e scommesse.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-brand-red shrink-0 mt-0.5" />
                <p className="text-xs text-white/50 leading-relaxed">
                  Il gioco è vietato ai minori di 18 anni e può causare dipendenza patologica.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
